import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'ramda'
import { DisplayTable } from '@/features/Application/elements/tables'
import {
  LabeledValue,
  LabelText,
  ValueText,
  Wrapper,
  EmptyComponent
} from '@/features/Application/elements'

const AutomobilesView = ({ application = {}, isLocked, applicationLoading }) => {
  const { t } = useTranslation()
  const automobiles = application.user_vehicles ?? []
  const title = t('r.profile.view.automobile.subtitle', 'Automobile Information')
  const ownershipLabels = {
    lease: t('r.profile.edit.automobile_ownership_lease.option', 'Lease'),
    finance: t('r.profile.edit.automobile_ownership_finance.option', 'Finance'),
    own: t('r.profile.edit.automobile_ownership_own.option', 'Own')
  }

  const data = useMemo(() => {
    if (isEmpty(automobiles)) return []
    return automobiles.map(automobile => ({
      items: [
        {
          id: 'Make and Model',
          cell: (
            <LabeledValue
              key="Make and Model"
              label={
                <LabelText>
                  {t('l.application.view.make_and_model.label', 'Make and Model')}
                </LabelText>
              }
              value={
                <ValueText isLocked={isLocked}>
                  {[automobile?.make, automobile?.model]?.filter(name => !!name).join(' ')}
                </ValueText>
              }
            />
          )
        },
        {
          id: 'Year',
          cell: (
            <LabeledValue
              key="Year"
              label={<LabelText>{t('l.application.view.year.label', 'Year')}</LabelText>}
              value={
                <ValueText isLocked={isLocked}>
                  {automobile?.model_year?.toString() || '-'}
                </ValueText>
              }
            />
          )
        },
        {
          id: 'Ownership Type',
          cell: (
            <LabeledValue
              key="Ownership Type"
              label={
                <LabelText>
                  {t('l.application.view.ownership_type.label', 'Ownership Type')}
                </LabelText>
              }
              value={
                <ValueText isLocked={isLocked}>
                  {ownershipLabels[automobile?.ownership] || '-'}
                </ValueText>
              }
            />
          )
        },
        {
          id: 'License Plate',
          cell: (
            <LabeledValue
              key="License Plate"
              label={
                <LabelText>
                  {t('l.application.view.license_plate.label', 'License Plate')}
                </LabelText>
              }
              value={
                <ValueText isLocked={isLocked}>
                  {automobile?.licence_plate?.toString() || '-'}
                </ValueText>
              }
            />
          )
        },
        {
          id: 'Colour',
          cell: (
            <LabeledValue
              key="Colour"
              label={<LabelText>{t('l.application.view.colour.label', 'Colour')}</LabelText>}
              value={
                <ValueText isLocked={isLocked}>{automobile?.colour?.toString() || '-'}</ValueText>
              }
            />
          )
        },
        {
          id: 'Province',
          cell: (
            <LabeledValue
              key="Province"
              label={<LabelText>{t('l.application.view.province.label', 'Province')}</LabelText>}
              value={
                <ValueText isLocked={isLocked}>{automobile?.state?.toString() || '-'}</ValueText>
              }
            />
          )
        },
        {
          id: 'Registered Owner',
          cell: (
            <LabeledValue
              key="Registered Owner"
              label={
                <LabelText>
                  {t('l.application.view.registered_owner.label', 'Registered Owner')}
                </LabelText>
              }
              value={
                <ValueText isLocked={isLocked}>
                  {automobile?.registered_owner?.toString() || '-'}
                </ValueText>
              }
            />
          )
        }
      ]
    }))
  }, [applicationLoading])

  return (
    <Wrapper>
      <DisplayTable
        data={data}
        title={title}
        columns={1}
        subColumns={2}
        emptyComponent={
          <EmptyComponent isLoading={applicationLoading}>
            {t(
              'l.application.view.no_automobiles.label',
              'No automobile included in this application.'
            )}
          </EmptyComponent>
        }
      />
    </Wrapper>
  )
}

export default AutomobilesView
